import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import { FLOW_TYPES } from 'e2e-tests/mocks/flows/flows.types';
import { MOCK_ROOM_FOR_USE_ACTIVE_ROOM } from 'e2e-tests/mocks/mock-rooms';
import { __MOCK_GUARDS__, mockRunKey } from 'e2e-tests/mocks/wagmi.mocks';
import { Analytics } from 'lib/AnalyticsLoader/analytics';

import useRevealLogic from 'modules/Room/Hooks/useRevealLogic';
import { useRoundInfo } from 'modules/Room/Hooks/useRoundInfo';

import { ROUTES } from 'shared/constants';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { CloserEnum, RoomStatusEnum } from 'shared/constants/shared-enums';
import { useAccount, useBlockNumber, usePublicClient } from 'shared/hooks/use-web3';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useGlobalStore } from 'shared/store/globalStore';
import { usePlayerGamesStore } from 'shared/store/playerGamesStore';
import { useRoomStore } from 'shared/store/roomStore';
import { isMocked } from 'shared/utils/isMocked';
import { readRoom } from 'shared/viem-functions/read-game-function';

import { runMockFlowByType } from './../../../../e2e-tests/mocks/flows/index';

const searchParams = new URLSearchParams(window.location.search);
const MOCK_FLOW_TYPE = searchParams.get('flow') ?? FLOW_TYPES.HAPPY_FLOW;

export const useActiveRoom = () => {
  const { activeRoomId } = useParams();
  useRoundInfo();
  useRevealLogic();
  const { invitedBy } = useGlobalStore();

  const {
    setRoom,
    roomStatus,
    refetchRoom,
    joinRoomId,
    setJoinRoomId,
    // optional in some test doubles
    setRefetchRoom,
  } = useRoomStore();

  const { address, chainId } = useAccount();
  const { data: lastBlock } = useBlockNumber();

  useEffect(() => {
    if (lastBlock) {
      Sentry.setTag('blockNumber', lastBlock);
    }
  }, [lastBlock]);

  const { setSelectedCurrency, availableCurrencies } = useCurrenciesStore();

  const navigate = useNavigate();
  const publicClient = usePublicClient();

  /**
   * Tracks the last round we triggered the mock flow for.
   * - In mock mode, if mock room exposes a `roundCounter`, we use it.
   * - If not, we fall back to "once per mount" behavior.
   */
  const lastMockRoundRef = useRef<number | string | null>(null);

  const setRoomValues = useCallback(async () => {
    if (isMocked) {
      const round = (MOCK_ROOM_FOR_USE_ACTIVE_ROOM as any)?.roundCounter ?? 'NO_ROUND_FIELD';
      const key = mockRunKey(activeRoomId ?? null, round);

      // Already completed for this room+round
      if (__MOCK_GUARDS__.runsPerRound.has(key)) return;

      const pending = __MOCK_GUARDS__.inFlight.get(key);
      if (pending) {
        await pending;
        return;
      }

      // Single in-flight runner
      const runner = (async () => {
        try {
          __MOCK_GUARDS__.runsPerRound.add(key);
          setRoom(MOCK_ROOM_FOR_USE_ACTIVE_ROOM);

          // Optional: stage guard if your mock script emits multiple stage logs
          __MOCK_GUARDS__.lastStageByKey.set(key, -1);

          await Promise.resolve(runMockFlowByType(MOCK_FLOW_TYPE));
        } finally {
          __MOCK_GUARDS__.inFlight.delete(key);
        }
      })();

      __MOCK_GUARDS__.inFlight.set(key, runner);
      await runner;
      return;
    }

    if (!publicClient) return;

    let dataFromContract: any;
    try {
      dataFromContract = await readRoom({
        provider: publicClient,
        roomId: BigInt(activeRoomId ?? 0),
      });
    } catch (err) {
      Sentry.captureException(err);
      return;
    }

    if (!Array.isArray(dataFromContract) || dataFromContract.length < 8) {
      Sentry.captureException(new Error('Invalid room data from contract'));
      return;
    }

    const [data, pot, roundCounter, winsCounter, closer, deadline, status, winnerStatus] =
      dataFromContract;

    if (
      data?.playerA.toLowerCase() === NULL_ADDRESS &&
      data?.playerB.toLowerCase() === NULL_ADDRESS
    ) {
      Sentry.captureException(new Error('Room not found'));
      navigate(ROUTES.LOBBY);
      return;
    }

    if (
      address &&
      data?.playerB.toLowerCase() !== NULL_ADDRESS &&
      status === RoomStatusEnum.Closed
    ) {
      const key = `${activeRoomId}${address}${chainId}`;
      usePlayerGamesStore.getState().deletePlayMove(key);

      Analytics.successGameEvent(address, invitedBy);
    }

    const playerARoom = {
      address: data?.playerA.toLowerCase() as `0x${string}`,
      wins: BigInt(winsCounter?.winsPlayerA ?? 0),
    };

    const amIPlayerA = playerARoom.address === address?.toLowerCase();
    const iAmConnectedPlayerB =
      data?.playerB.toLowerCase() === NULL_ADDRESS && !amIPlayerA && joinRoomId;
    if (joinRoomId) setJoinRoomId(null);
    const playerBRoom = {
      address: (iAmConnectedPlayerB
        ? address?.toLowerCase()
        : data?.playerB.toLowerCase()) as `0x${string}`,
      wins: BigInt(winsCounter?.winsPlayerB ?? 0),
    };
    const someoneElseGame =
      !amIPlayerA &&
      playerBRoom.address !== address?.toLowerCase() &&
      (data?.playerB.toLowerCase() !== NULL_ADDRESS || !!winnerStatus);

    const currencyObject = availableCurrencies.find(
      (c) => c.address.toLowerCase() === data.token.toLowerCase(),
    );

    if (currencyObject) {
      setSelectedCurrency(currencyObject.symbol);
      Sentry.setTag('roomId', activeRoomId);
      Sentry.setTag('roomStatus', status);
      Sentry.setTag('wallet', address);
      Sentry.setTag('roundCounter', roundCounter);
      Sentry.setTag('winnerStatus', winnerStatus);
      Sentry.setTag('closer', closer);
      Sentry.setTag('deadline', deadline);

      setRoom({
        winsTo: BigInt(data.wins),
        bet: Number(Number(data.bet) / Number(10 ** currencyObject.decimals)),
        roomBalance: Number(pot) / 10 ** currencyObject.decimals,
        roomStatus: status,
        playerA: { ...playerARoom },
        playerB: { ...playerBRoom },
        roundCounter,
        winnerStatus,
        closerRoom: { closer: closer as CloserEnum, deadline },
        amIPlayerA,
        someoneElseGame,
      });

      // Keep ref in sync if we ever toggle mock/real across renders
      lastMockRoundRef.current = Number(roundCounter);
    }
  }, [
    activeRoomId,
    address,
    availableCurrencies,
    chainId,
    invitedBy,
    joinRoomId,
    navigate,
    publicClient,
    setRoom,
    setSelectedCurrency,
    setJoinRoomId,
  ]);

  // Run once & when dependencies change
  useEffect(() => {
    setRoomValues();
  }, [publicClient, setRoomValues]);

  // Expose refetch function to the store (some tests expect this)
  useEffect(() => {
    if (typeof setRefetchRoom === 'function') {
      setRefetchRoom(setRoomValues);
    }
  }, [setRefetchRoom, setRoomValues]);

  // Polling while room is open
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (isMocked) return;

    if (activeRoomId && roomStatus !== RoomStatusEnum.Closed && publicClient) {
      intervalRef.current = setInterval(() => {
        refetchRoom();
        setRoomValues();
      }, 3000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [activeRoomId, roomStatus, refetchRoom, publicClient, setRoomValues]);
};
